import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';
import { BACKEND_URL } from '../config';
import { TemplateIcon } from '../utils/iconResolver';

import {
  ArrowLeft,
  ArrowSquareOut,
  Calendar,
  DownloadSimple,
  Play,
  Robot,
  User
} from '@phosphor-icons/react';
const CreatorProfile = () => {
  const { username } = useParams();
  const [creator, setCreator] = useState(null);
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCreator = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/creators/${username}`);
        if (!res.ok) throw new Error('Creator not found');
        const data = await res.json();
        setCreator(data.creator);
        setAutomations(data.automations || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchCreator();
  }, [username]);

  if (loading) {
    return (
      <>
        <LandingHeader />
        <div className="min-h-screen bg-[#0B0D11] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#6366F1] border-t-transparent rounded-full animate-spin" />
        </div>
      </>
    );
  }

  if (!creator) {
    return (
      <>
        <LandingHeader />
        <div className="min-h-screen bg-[#0B0D11] flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-white mb-2">Creator not found</h1>
            <Link to="/automations" className="text-[#6366F1] hover:underline text-sm">Browse automations</Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LandingHeader />
      <div className="min-h-screen bg-[#0B0D11] pt-24 pb-16 px-4">
        <div className="max-w-5xl mx-auto">
          {/* Back */}
          <Link to="/automations" className="inline-flex items-center gap-2 text-[#8b8fa3] hover:text-white text-sm mb-8 transition-colors" data-testid="back-to-gallery">
            <ArrowLeft size={16} /> Back to Gallery
          </Link>

          {/* Profile Header */}
          <div className="glass rounded-2xl p-8 mb-8" data-testid="creator-profile-header">
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#6366F1] to-[#EC4899] flex items-center justify-center shrink-0">
                {creator.avatar ? (
                  <img src={creator.avatar} alt={creator.display_name} className="w-20 h-20 rounded-2xl object-cover" />
                ) : (
                  <User size={36} className="text-white" />
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-white mb-1">{creator.display_name}</h1>
                <p className="text-[#8b8fa3] text-sm mb-3">@{creator.username}</p>
                {creator.bio && <p className="text-[#A0AEC0] text-sm max-w-2xl">{creator.bio}</p>}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mt-8" data-testid="creator-stats">
              <div className="glass rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-white">{creator.total_automations || 0}</p>
                <p className="text-xs text-[#8b8fa3] mt-1">Automations</p>
              </div>
              <div className="glass rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-white">{(creator.total_installs || 0).toLocaleString()}</p>
                <p className="text-xs text-[#8b8fa3] mt-1">Total Installs</p>
              </div>
              <div className="glass rounded-xl p-4 text-center">
                <p className="text-2xl font-bold text-white">{(creator.total_runs || 0).toLocaleString()}</p>
                <p className="text-xs text-[#8b8fa3] mt-1">Total Runs</p>
              </div>
            </div>
          </div>

          {/* Automations */}
          <h2 className="text-lg font-semibold text-white mb-4">Published Automations</h2>
          {automations.length === 0 ? (
            <div className="glass rounded-2xl p-12 text-center">
              <Robot size={40} className="text-[#555] mx-auto mb-3" />
              <p className="text-[#8b8fa3]">No published automations yet</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {automations.map(a => (
                <Link
                  key={a.id || a.slug}
                  to={`/automations/${a.slug}`}
                  className="glass rounded-xl p-5 hover:border-[rgba(99,102,241,0.3)] border border-transparent transition-all group"
                  data-testid={`creator-automation-${a.slug}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: `${a.color || '#6366F1'}20` }}
                    >
                      <TemplateIcon icon={a.icon} size={20} className="text-indigo-400" />
                    </div>
                    <h3 className="text-white font-medium text-sm group-hover:text-[#6366F1] transition-colors line-clamp-1">{a.name}</h3>
                  </div>
                  <p className="text-[#8b8fa3] text-xs line-clamp-2 mb-4">{a.tagline || a.short_description}</p>
                  <div className="flex items-center gap-4 text-[10px] text-[#6b7080]">
                    <span className="flex items-center gap-1"><DownloadSimple size={10} /> {a.install_count || 0}</span>
                    <span className="flex items-center gap-1"><Play size={10} /> {a.run_count || a.usage_count || 0}</span>
                    {a.category && <span className="px-1.5 py-0.5 rounded bg-[rgba(255,255,255,0.05)]">{a.category}</span>}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default CreatorProfile;
